import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { applyLoginSession, logoutAuthSession } from "../authApi";
import { getStoredAccessToken } from "../authSession";
import { isJwtExpired } from "../lib/jwt";
import { isSupabaseAuthConfigured, supabase } from "../lib/supabaseClient";

export default function RequireAuth({ children }) {
  const location = useLocation();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let mounted = true;
    let unsubscribe = null;

    async function bootstrap() {
      const storedToken = getStoredAccessToken();
      const tokenIsFresh = storedToken && !isJwtExpired(storedToken);

      if (tokenIsFresh) {
        if (mounted) setStatus("authed");
      } else if (!isSupabaseAuthConfigured || !supabase) {
        if (mounted) setStatus("missing-config");
      } else {
        const { data, error } = await supabase.auth.getSession();
        if (!mounted) return;
        const session = data?.session;
        if (!error && session?.access_token && !isJwtExpired(session.access_token)) {
          applyLoginSession({
            accessToken: session.access_token,
            refreshToken: session.refresh_token,
            persist: true
          });
          setStatus("authed");
        } else {
          logoutAuthSession();
          setStatus("unauth");
        }
      }

      if (isSupabaseAuthConfigured && supabase) {
        const listener = supabase.auth.onAuthStateChange((_event, session) => {
          if (!mounted) return;
          if (session?.access_token) {
            applyLoginSession({
              accessToken: session.access_token,
              refreshToken: session.refresh_token,
              persist: true
            });
            setStatus("authed");
          } else {
            logoutAuthSession();
            setStatus("unauth");
          }
        });
        unsubscribe = listener?.data?.subscription?.unsubscribe || null;
      }
    }

    bootstrap();
    return () => {
      mounted = false;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  if (status === "checking") {
    return (
      <div className="min-h-dvh grid place-items-center bg-surface text-ink p-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-10 h-10 rounded-2xl bg-brand-subtle text-brand grid place-items-center animate-pulse">
            <span className="material-symbols-outlined">trophy</span>
          </div>
          <p className="font-display text-base font-semibold">Restoring your workspace…</p>
          <p className="text-xs text-ink-muted">One moment.</p>
        </div>
      </div>
    );
  }

  if (status === "missing-config") {
    return (
      <div className="min-h-dvh grid place-items-center bg-surface text-ink p-6">
        <div className="surface-card p-6 sm:p-8 max-w-md text-center">
          <h1 className="font-display text-xl font-semibold">Auth not configured</h1>
          <p className="text-sm text-ink-muted mt-2">
            Add <span className="font-mono text-xs">VITE_SUPABASE_URL</span> and{" "}
            <span className="font-mono text-xs">VITE_SUPABASE_ANON_KEY</span> to your{" "}
            <span className="font-mono text-xs">runtime-config.js</span> to enable sign-in.
          </p>
        </div>
      </div>
    );
  }

  if (status !== "authed") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}
